import React, { useEffect, useState } from 'react';
import { Loader2, Save, Plus, Trash2, Image as ImageIcon, Link as LinkIcon, ShieldCheck, Sparkles } from 'lucide-react';
import { adminApi, formatApiErrorDetail } from './api';
import { useLanguage } from '../context/LanguageContext';

/**
 * AdminGlobal — the last-mile CMS screen covering Phase C content:
 *   • branding       — Header logo (light / dark)
 *   • footer         — Quick Links & Services link columns
 *   • values_cards   — Home > Values 6-card content (title + description × 2 langs)
 *   • privacy_policy — /privacy-policy page content (nested sections)
 *
 * Every sub-form has its own dirty state + Save button so operators can edit
 * one area without touching the others.
 */
const inputCls =
  'w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-blue/40';

const Field = ({ label, dir, children, hint }) => (
  <div>
    <label
      dir={dir}
      className={`block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
    >
      {label}
    </label>
    {children}
    {hint && <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">{hint}</p>}
  </div>
);

const Card = ({ Icon, titleAr, titleEn, isAr, subtitle, testId, onSave, saving, children }) => (
  <section
    data-testid={testId}
    className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 md:p-6 space-y-4"
  >
    <div className={`flex items-center gap-3 ${isAr ? 'flex-row-reverse text-right' : ''}`}>
      <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-blue/15 to-brand-green/15 border border-brand-blue/25 flex items-center justify-center">
        <Icon className="w-5 h-5 text-brand-blue-700 dark:text-brand-blue-300" />
      </span>
      <div className="flex-1">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white">{isAr ? titleAr : titleEn}</h2>
        {subtitle && <p className="text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>}
      </div>
    </div>
    {children}
    <div className={`flex ${isAr ? 'justify-start' : 'justify-end'}`}>
      <button
        type="button"
        data-testid={`${testId}-save`}
        onClick={onSave}
        disabled={saving}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-brand-blue to-brand-blue-700 text-white text-sm font-semibold shadow disabled:opacity-60"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {isAr ? 'حفظ' : 'Save'}
      </button>
    </div>
  </section>
);

// -------- Reusable link-list editor for Footer columns --------
const LinkListEditor = ({ items, setItems, isAr, testId }) => {
  const list = Array.isArray(items) ? items : [];
  const add = () => setItems([...list, { label_ar: '', label_en: '', href: '' }]);
  const remove = (i) => setItems(list.filter((_, idx) => idx !== i));
  const update = (i, key, val) => setItems(list.map((it, idx) => idx === i ? { ...it, [key]: val } : it));

  return (
    <div className="space-y-3">
      {list.length === 0 && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {isAr ? 'لا توجد روابط — أضف واحداً لبدء التخصيص، أو اترك القائمة فارغة لاستخدام الروابط الافتراضية.' : 'No links — add one to start customising, or leave the list empty to use the site defaults.'}
        </p>
      )}
      {list.map((it, i) => (
        <div key={i} data-testid={`${testId}-item-${i}`} className={`rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 p-4 ${isAr ? 'text-right' : 'text-left'}`}>
          <div className="grid md:grid-cols-3 gap-3">
            <Field label={isAr ? 'التسمية (عربي)' : 'Label (Arabic)'} dir="rtl">
              <input dir="rtl" className={inputCls} value={it.label_ar || ''} onChange={(e) => update(i, 'label_ar', e.target.value)} />
            </Field>
            <Field label={isAr ? 'التسمية (إنجليزي)' : 'Label (English)'} dir="ltr">
              <input dir="ltr" className={inputCls} value={it.label_en || ''} onChange={(e) => update(i, 'label_en', e.target.value)} />
            </Field>
            <Field label={isAr ? 'الرابط (URL أو anchor)' : 'URL (or anchor)'} dir="ltr">
              <input dir="ltr" className={inputCls} value={it.href || ''} onChange={(e) => update(i, 'href', e.target.value)} placeholder="/services or #services or https://…" />
            </Field>
          </div>
          <div className={`mt-3 flex ${isAr ? 'justify-start' : 'justify-end'}`}>
            <button type="button" onClick={() => remove(i)} data-testid={`${testId}-remove-${i}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 text-xs font-semibold">
              <Trash2 className="w-3.5 h-3.5" />
              {isAr ? 'حذف' : 'Remove'}
            </button>
          </div>
        </div>
      ))}
      <button type="button" onClick={add} data-testid={`${testId}-add`} className="w-full py-3 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 text-sm text-slate-500 dark:text-slate-400 hover:border-brand-blue hover:text-brand-blue-700">
        <Plus className="inline w-4 h-4 mr-1.5" />
        {isAr ? 'إضافة رابط' : 'Add link'}
      </button>
    </div>
  );
};

// -------- Values card editor (6 items, fixed length) --------
const VALUES_LABELS_AR = ['الاستدامة', 'الشفافية', 'النزاهة', 'الجودة', 'العدالة', 'الابتكار'];
const VALUES_LABELS_EN = ['Sustainability', 'Transparency', 'Integrity', 'Quality', 'Fairness', 'Innovation'];

const ValuesCardsEditor = ({ items, setItems, isAr }) => {
  const list = Array.isArray(items) ? items : [];
  const ensured = Array.from({ length: 6 }, (_, i) => list[i] || {});
  const update = (i, key, val) => {
    const next = ensured.slice();
    next[i] = { ...next[i], [key]: val };
    setItems(next);
  };
  return (
    <div className="space-y-3">
      {ensured.map((it, i) => (
        <div key={i} data-testid={`values-card-${i}`} className={`rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 p-4 ${isAr ? 'text-right' : 'text-left'}`}>
          <div className={`text-xs font-bold text-brand-blue-700 dark:text-brand-blue-300 mb-2 ${isAr ? 'text-right' : ''}`}>
            {`0${i + 1} · ${isAr ? VALUES_LABELS_AR[i] : VALUES_LABELS_EN[i]}`}
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label={isAr ? 'العنوان (عربي)' : 'Title (Arabic)'} dir="rtl">
              <input dir="rtl" className={inputCls} value={it.title_ar || ''} onChange={(e) => update(i, 'title_ar', e.target.value)} />
            </Field>
            <Field label={isAr ? 'العنوان (إنجليزي)' : 'Title (English)'} dir="ltr">
              <input dir="ltr" className={inputCls} value={it.title_en || ''} onChange={(e) => update(i, 'title_en', e.target.value)} />
            </Field>
          </div>
          <div className="grid md:grid-cols-2 gap-3 mt-3">
            <Field label={isAr ? 'الوصف (عربي)' : 'Description (Arabic)'} dir="rtl">
              <textarea rows={3} dir="rtl" className={inputCls} value={it.description_ar || ''} onChange={(e) => update(i, 'description_ar', e.target.value)} />
            </Field>
            <Field label={isAr ? 'الوصف (إنجليزي)' : 'Description (English)'} dir="ltr">
              <textarea rows={3} dir="ltr" className={inputCls} value={it.description_en || ''} onChange={(e) => update(i, 'description_en', e.target.value)} />
            </Field>
          </div>
        </div>
      ))}
    </div>
  );
};

// -------- Privacy Policy editor --------
const PrivacyPolicyEditor = ({ data, setData, isAr }) => {
  const patch = (key, val) => setData({ ...data, [key]: val });
  const setBi = (key) => (v) => patch(key, v);

  const sections = Array.isArray(data.sections) ? data.sections : [];
  const setSection = (i, patchObj) => {
    const next = sections.slice();
    next[i] = { ...next[i], ...patchObj };
    patch('sections', next);
  };
  const addSection = () => patch('sections', [...sections, { n: String(sections.length + 1), h_ar: '', h_en: '', p_ar: '', p_en: '', items_ar: [], items_en: [] }]);
  const removeSection = (i) => patch('sections', sections.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-4">
      {/* Top-level bilingual fields */}
      <PairInput label={isAr ? 'الشعار الصغير' : 'Eyebrow'} value={data.eyebrow} onChange={setBi('eyebrow')} isAr={isAr} />
      <PairInput label={isAr ? 'العنوان' : 'Title'}       value={data.title}   onChange={setBi('title')}   isAr={isAr} />
      <PairInput label={isAr ? 'اسم الشركة' : 'Company name'} value={data.company} onChange={setBi('company')} isAr={isAr} />
      <PairBody  label={isAr ? 'المقدمة' : 'Intro paragraph'} value={data.intro}   onChange={setBi('intro')}   isAr={isAr} rows={4} />
      <PairInput label={isAr ? 'تاريخ آخر تحديث' : 'Last-updated line'} value={data.last_updated} onChange={setBi('last_updated')} isAr={isAr} />

      {/* Sections */}
      <div>
        <p className={`text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2 ${isAr ? 'text-right' : 'text-left'}`}>
          {isAr ? 'الأقسام' : 'Sections'}
        </p>
        <p className={`text-[11px] text-slate-500 dark:text-slate-400 mb-3 ${isAr ? 'text-right' : 'text-left'}`}>
          {isAr ? 'اترك قائمة الأقسام فارغة لاستخدام النص القانوني الافتراضي.' : 'Leave the sections list empty to use the default legal text.'}
        </p>
        <div className="space-y-3">
          {sections.map((s, i) => (
            <div key={i} data-testid={`privacy-section-${i}`} className={`rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 p-4 ${isAr ? 'text-right' : 'text-left'}`}>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label={isAr ? 'الترقيم' : 'Number'} dir="ltr">
                  <input dir="ltr" className={inputCls} value={s.n || ''} onChange={(e) => setSection(i, { n: e.target.value })} />
                </Field>
              </div>
              <PairInput label={isAr ? 'عنوان القسم' : 'Section heading'} value={{ ar: s.h_ar || '', en: s.h_en || '' }}
                onChange={(v) => setSection(i, { h_ar: v.ar, h_en: v.en })} isAr={isAr} />
              <PairBody label={isAr ? 'الفقرة' : 'Paragraph'} value={{ ar: s.p_ar || '', en: s.p_en || '' }}
                onChange={(v) => setSection(i, { p_ar: v.ar, p_en: v.en })} isAr={isAr} rows={3} />

              <div className="grid md:grid-cols-2 gap-3 mt-3">
                <Field label={isAr ? 'قائمة نقاط (عربي — كل سطر عنصر)' : 'Bullet list (Arabic — one per line)'} dir="rtl">
                  <textarea rows={3} dir="rtl" className={inputCls} value={(s.items_ar || []).join('\n')} onChange={(e) => setSection(i, { items_ar: e.target.value.split('\n').map((x) => x.trim()).filter(Boolean) })} />
                </Field>
                <Field label={isAr ? 'قائمة نقاط (إنجليزي — كل سطر عنصر)' : 'Bullet list (English — one per line)'} dir="ltr">
                  <textarea rows={3} dir="ltr" className={inputCls} value={(s.items_en || []).join('\n')} onChange={(e) => setSection(i, { items_en: e.target.value.split('\n').map((x) => x.trim()).filter(Boolean) })} />
                </Field>
              </div>

              <div className={`mt-3 flex ${isAr ? 'justify-start' : 'justify-end'}`}>
                <button type="button" onClick={() => removeSection(i)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 text-xs font-semibold">
                  <Trash2 className="w-3.5 h-3.5" />
                  {isAr ? 'حذف القسم' : 'Remove section'}
                </button>
              </div>
            </div>
          ))}
          <button type="button" onClick={addSection} data-testid="privacy-add-section" className="w-full py-3 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 text-sm text-slate-500 dark:text-slate-400 hover:border-brand-blue hover:text-brand-blue-700">
            <Plus className="inline w-4 h-4 mr-1.5" />
            {isAr ? 'إضافة قسم' : 'Add section'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PairInput = ({ label, value, onChange, isAr }) => {
  const v = value || {};
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <Field label={`${label} (${isAr ? 'عربي' : 'Arabic'})`} dir="rtl">
        <input dir="rtl" className={inputCls} value={v.ar || ''} onChange={(e) => onChange({ ...v, ar: e.target.value })} />
      </Field>
      <Field label={`${label} (${isAr ? 'إنجليزي' : 'English'})`} dir="ltr">
        <input dir="ltr" className={inputCls} value={v.en || ''} onChange={(e) => onChange({ ...v, en: e.target.value })} />
      </Field>
    </div>
  );
};
const PairBody = ({ label, value, onChange, isAr, rows = 4 }) => {
  const v = value || {};
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <Field label={`${label} (${isAr ? 'عربي' : 'Arabic'})`} dir="rtl">
        <textarea rows={rows} dir="rtl" className={inputCls} value={v.ar || ''} onChange={(e) => onChange({ ...v, ar: e.target.value })} />
      </Field>
      <Field label={`${label} (${isAr ? 'إنجليزي' : 'English'})`} dir="ltr">
        <textarea rows={rows} dir="ltr" className={inputCls} value={v.en || ''} onChange={(e) => onChange({ ...v, en: e.target.value })} />
      </Field>
    </div>
  );
};

// -------- Main screen --------
const KEYS = ['branding', 'footer', 'values_cards', 'privacy_policy'];

const AdminGlobal = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [data, setData] = useState(() => Object.fromEntries(KEYS.map((k) => [k, {}])));
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const results = await Promise.all(KEYS.map((k) => adminApi.get(`/settings/${k}`)));
      const next = {};
      KEYS.forEach((k, i) => { next[k] = (results[i].data && results[i].data.value) || {}; });
      setData(next);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const set = (key, updater) => setData((prev) => ({
    ...prev,
    [key]: typeof updater === 'function' ? updater(prev[key] || {}) : updater,
  }));

  const saveOne = async (key) => {
    setSavingKey(key); setError(''); setMsg('');
    try {
      await adminApi.put(`/settings/${key}`, { value: data[key] || {} });
      setMsg(isAr ? 'تم الحفظ بنجاح' : 'Saved successfully');
      setTimeout(() => setMsg(''), 2200);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSavingKey(''); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>;
  }

  const branding    = data.branding    || {};
  const footer      = data.footer      || {};
  const valuesCards = data.values_cards || {};
  const privacy     = data.privacy_policy || {};

  return (
    <div className="space-y-6" data-testid="admin-global-page" dir={isAr ? 'rtl' : 'ltr'}>
      <div className={isAr ? 'text-right' : 'text-left'}>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
          {isAr ? 'المحتوى العام' : 'Global Content'}
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {isAr
            ? 'شعار الهوية، أعمدة روابط الفوتر، بطاقات القيم الستة، وسياسة الخصوصية — كل حقل يعود إلى القيمة الافتراضية إذا تُرك فارغاً.'
            : 'Brand logo, footer link columns, the six Values cards, and Privacy Policy — every field falls back to the built-in default when left blank.'}
        </p>
      </div>
      {error && <div className="px-4 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 text-sm">{error}</div>}
      {msg && <div className="px-4 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-sm">{msg}</div>}

      {/* ---------- BRANDING ---------- */}
      <Card
        Icon={ImageIcon}
        isAr={isAr}
        testId="global-branding"
        titleAr="شعار الهوية"
        titleEn="Brand Logo"
        subtitle={isAr ? 'يظهر في رأس الصفحة. اترك أحد الحقلين فارغاً لاستخدام الآخر في كلا الوضعين.' : 'Shown in the header. Leave one blank to reuse the other in both themes.'}
        onSave={() => saveOne('branding')}
        saving={savingKey === 'branding'}
      >
        <div className="grid md:grid-cols-2 gap-3">
          <Field label={isAr ? 'شعار (Light) — URL' : 'Logo (Light) — URL'} dir="ltr">
            <input dir="ltr" className={inputCls} value={branding.logo_light || ''} onChange={(e) => set('branding', { ...branding, logo_light: e.target.value })} />
          </Field>
          <Field label={isAr ? 'شعار (Dark) — URL' : 'Logo (Dark) — URL'} dir="ltr">
            <input dir="ltr" className={inputCls} value={branding.logo_dark || ''} onChange={(e) => set('branding', { ...branding, logo_dark: e.target.value })} />
          </Field>
        </div>
      </Card>

      {/* ---------- FOOTER LINKS ---------- */}
      <Card
        Icon={LinkIcon}
        isAr={isAr}
        testId="global-footer"
        titleAr="أعمدة روابط الفوتر"
        titleEn="Footer Link Columns"
        subtitle={isAr ? 'أعمدة "روابط سريعة" و"خدماتنا". اترك الأعمدة فارغة لاستخدام الروابط الافتراضية.' : '“Quick Links” and “Services” columns. Empty = default links.'}
        onSave={() => saveOne('footer')}
        saving={savingKey === 'footer'}
      >
        <div>
          <p className={`text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2 ${isAr ? 'text-right' : ''}`}>{isAr ? 'الروابط السريعة' : 'Quick Links'}</p>
          <LinkListEditor items={footer.quick_links || []} setItems={(v) => set('footer', { ...footer, quick_links: v })} isAr={isAr} testId="footer-quick" />
        </div>
        <div className="pt-4 border-t border-slate-200 dark:border-slate-800">
          <p className={`text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2 ${isAr ? 'text-right' : ''}`}>{isAr ? 'الخدمات' : 'Services'}</p>
          <LinkListEditor items={footer.services || []} setItems={(v) => set('footer', { ...footer, services: v })} isAr={isAr} testId="footer-services" />
        </div>
      </Card>

      {/* ---------- VALUES 6 CARDS ---------- */}
      <Card
        Icon={Sparkles}
        isAr={isAr}
        testId="global-values"
        titleAr="بطاقات القيم (6)"
        titleEn="Values (6 cards)"
        subtitle={isAr ? 'العناوين والأوصاف قابلة للتحرير. الأيقونات والتدرجات ثابتة (قيود التصميم).' : 'Titles and descriptions are editable. Icons and gradients are fixed by design.'}
        onSave={() => saveOne('values_cards')}
        saving={savingKey === 'values_cards'}
      >
        <ValuesCardsEditor items={valuesCards.items || []} setItems={(v) => set('values_cards', { ...valuesCards, items: v })} isAr={isAr} />
      </Card>

      {/* ---------- PRIVACY POLICY ---------- */}
      <Card
        Icon={ShieldCheck}
        isAr={isAr}
        testId="global-privacy"
        titleAr="سياسة الخصوصية"
        titleEn="Privacy Policy"
        subtitle={isAr ? 'محرر ثنائي اللغة كامل — عناوين، مقدمة، أقسام مرقمة، ونقاط سياسة.' : 'Full bilingual editor — headings, intro, numbered sections, and bullet items.'}
        onSave={() => saveOne('privacy_policy')}
        saving={savingKey === 'privacy_policy'}
      >
        <PrivacyPolicyEditor data={privacy} setData={(v) => set('privacy_policy', v)} isAr={isAr} />
      </Card>
    </div>
  );
};

export default AdminGlobal;
